import type { ApolloCache } from "@apollo/client"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { FEATURE_TYPE_CODE } from "@/v2-backoffice-client/feature/feature.constants"
import { ACCESS_MODE_LABEL } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.accessMode.constants"
import { evictFeatureGroupQueries } from "@/v2-backoffice-client/license/license.utils"
import {
  seatPersonNameOf,
  type LicenseSeatPerson,
} from "@/v2-backoffice-client/license/seats/license.seats.utils"
import type { LicenseFeatureGroupChange } from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeFooter"
import type { CustomerFeatureAccessFragment } from "@/v2-backoffice-client/customer/featureAccess/__types__/customer.featureAccess.fragment"

/**
 * The three classes a user is checked against, by the name an operator
 * uses for them; the only classes a customer can take "only what's listed"
 * on (backoffice.Set_Lic_CustomerFeatureAccess refuses the rest).
 */
export const CUSTOMER_FEATURE_ACCESS_CLASSES: readonly {
  featureTypeCode: number
  label: string
}[] = [
  { featureTypeCode: FEATURE_TYPE_CODE.mapLayer, label: "Map layers" },
  { featureTypeCode: FEATURE_TYPE_CODE.basemap, label: "Base maps" },
  {
    featureTypeCode: FEATURE_TYPE_CODE.uiComponent,
    label: "Fusion capabilities",
  },
]

export function classLabelOf(featureTypeCode: number) {
  return (
    CUSTOMER_FEATURE_ACCESS_CLASSES.find(
      (featureClass) => featureClass.featureTypeCode === featureTypeCode,
    )?.label ?? `feature type ${featureTypeCode}`
  )
}

function joinNames(names: readonly string[]) {
  if (names.length <= 1) return names.join("")
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
}

/** The staff customer's one sentence: the tab has nothing to set for it. */
export const STAFF_FEATURE_ACCESS_SENTENCE =
  "Staff. This is Placepoint itself: its users get every feature there is or ever will be, package-only or not, listed or not, so nothing in the catalog can block development. There is nothing to set here."

/**
 * The three places a customer's access comes from, as the headline lists
 * them: one row each, a tick (or a cross) and a sentence a salesperson can
 * read out. Together they ARE the rule for this customer; the lists below
 * are the same answer row by row.
 */
export type CustomerFeatureAccessSource = {
  key: "free" | "licenses" | "ownList"
  label: string
  /** False when the row contributes nothing right now (every class closed, no live license). */
  applies: boolean
  text: string
}

/**
 * What the carrier is and whom it reaches: "their Placepoint license" for a
 * license, "add-on, with their Placepoint MCP (Norway) license" for an
 * add-on. A seated product's license says which seat mode it is in: for
 * everyone at the customer (the default), or for its named people only
 * (FUS-2766). The API's Kind is "license" for a license (the catalog's
 * internal name) and "add-on" for an add-on.
 */
export function describeCarrierScope(
  carrier: CustomerFeatureAccessFragment["Carriers"][number],
) {
  const scope = !carrier.SeatsAreAssigned
    ? ""
    : carrier.SeatHoldersOnly
      ? ", for its named seat holders only"
      : ", for everyone at the customer"
  return carrier.Kind === "add-on"
    ? `add-on, with their ${carrier.ProductName} license${scope}`
    : `their ${carrier.ProductName} license${scope}`
}

/**
 * Who a limited carrier reaches, said in full under it: the holders by name
 * held against the license's seat count, or that every name belongs to
 * someone who left, so nobody at the customer can use it right now. The
 * names are what makes "named seat holders only" mean something to a
 * salesperson, and what ties the row to the "show access for" picker
 * beside it.
 */
export function describeCarrierSeatHolders(
  carrier: CustomerFeatureAccessFragment["Carriers"][number],
  holders: readonly LicenseSeatPerson[],
  customerName: string,
) {
  if (holders.length === 0) {
    return `Limited to named people, but none of them is an active user any more, so nobody at ${customerName} can use it. Name people or remove the limit on the Licensing tab (Seats).`
  }
  const cap = carrier.Seats
  const count =
    cap == null
      ? `${holders.length}`
      : holders.length > cap
        ? `${holders.length} of ${cap} seats, over the licensed count`
        : `${holders.length} of ${cap} ${cap === 1 ? "seat" : "seats"}`
  return `Seat holders (${count}): ${holders.map(seatPersonNameOf).join(", ")}`
}

/** The license row's link: what the Licensing tab changes for this customer. */
export function describeLicensingTabLink(
  access: CustomerFeatureAccessFragment,
) {
  const hasSeated = access.Carriers.some((carrier) => carrier.SeatsAreAssigned)
  return hasSeated
    ? "Update licenses, add-ons and seats on the Licensing tab"
    : "Update licenses and add-ons on the Licensing tab"
}

/** "Placepoint MCP (Norway) — their Placepoint MCP license, for everyone at the customer". */
export function describeCarrier(
  carrier: CustomerFeatureAccessFragment["Carriers"][number],
) {
  return `${carrier.Name} — ${describeCarrierScope(carrier)}`
}

export function describeFreeSource(
  access: CustomerFeatureAccessFragment,
): CustomerFeatureAccessSource {
  const closed = CUSTOMER_FEATURE_ACCESS_CLASSES.filter((featureClass) =>
    access.ClosedFeatureTypeCodes.includes(featureClass.featureTypeCode),
  )
  const since = access.ClosedAt
    ? ` (since ${formatDateString(access.ClosedAt)})`
    : ""
  if (closed.length === CUSTOMER_FEATURE_ACCESS_CLASSES.length) {
    return {
      key: "free",
      label: "Nothing by default",
      applies: false,
      text: `Every class is set to "${ACCESS_MODE_LABEL.listed}"${since}: their users get only what their licenses include and what is added below. Nothing else, and nothing new unless a ticked header rule covers it.`,
    }
  }
  const exception =
    closed.length > 0
      ? ` Except ${joinNames(closed.map((featureClass) => featureClass.label.toLowerCase()))}${since}: there they get only what's listed below.`
      : ""
  return {
    key: "free",
    label: "Free for everyone",
    applies: true,
    text: `Every feature that is not package-only. Every customer has these, and new ones arrive on their own.${exception}`,
  }
}

export function describeLicenseSource(
  access: CustomerFeatureAccessFragment,
): CustomerFeatureAccessSource {
  if (access.Carriers.length === 0) {
    return {
      key: "licenses",
      label: "Their licenses",
      applies: false,
      text:
        access.LiveLicenseCount + access.LiveSeatedLicenseCount === 0
          ? "No live license. Nothing from a license or add-on reaches them, and anything added below applies only once a license is live."
          : "Their live licenses include no packages, so nothing package-only reaches them through a license or add-on. Check the catalog license each grant is on (Licensing tab, Change License).",
    }
  }
  return {
    key: "licenses",
    label: "Their licenses",
    applies: true,
    text: access.Carriers.map(describeCarrier).join(" · "),
  }
}

export function describeOwnListSource(
  access: CustomerFeatureAccessFragment,
  customerName: string,
): CustomerFeatureAccessSource {
  const picks = access.OwnList?.FeatureCount ?? 0
  const rules = access.OwnList?.RuleCount ?? 0
  const count =
    picks + rules === 0
      ? "Nothing yet."
      : [
          picks > 0 ? `${picks} ticked` : null,
          rules > 0
            ? `${rules} header ${rules === 1 ? "rule" : "rules"}`
            : null,
        ]
          .filter(Boolean)
          .join(", ") + " today."
  const needsLicense =
    picks + rules > 0 && access.LiveLicenseCount === 0
      ? " Reaches nobody until a license is live."
      : ""
  return {
    key: "ownList",
    label: "Added here",
    applies: picks + rules > 0 && access.LiveLicenseCount > 0,
    text: `The boxes ticked below, for ${customerName} only. ${count}${needsLicense}`,
  }
}

/** The classes the customer takes "only what's listed" on: the tab's badge. */
export function closedClassCountOf(access: CustomerFeatureAccessFragment) {
  return access.ClosedFeatureTypeCodes.length
}

/**
 * The customer header's one line on access: what a CSM must know before any
 * tab is opened. "Everyone's access" for the common case; for a customer
 * that takes only what is listed, the classes it closed and how much its own
 * list holds, e.g. "Map layers: only what's listed · 43 ticked".
 */
export function describeAccessSummary(access: CustomerFeatureAccessFragment) {
  if (access.IsStaff) return "Staff: everything, present and future"
  if (access.ClosedFeatureTypeCodes.length === 0) return "Everyone's access"
  const classes = joinNames(
    access.ClosedFeatureTypeCodes.map((featureTypeCode) =>
      classLabelOf(featureTypeCode),
    ),
  )
  const picks = access.OwnList?.FeatureCount ?? 0
  const rules = access.OwnList?.RuleCount ?? 0
  const listed =
    picks + rules === 0
      ? "nothing listed yet"
      : [
          picks > 0 ? `${picks} ticked` : null,
          rules > 0
            ? `${rules} header ${rules === 1 ? "rule" : "rules"}`
            : null,
        ]
          .filter(Boolean)
          .join(", ")
  const carried =
    access.LiveLicenseCount === 0
      ? " · reaches nobody until a license is live"
      : ""
  return `${classes}: only what's listed · ${listed}${carried}`
}

/** The footer's rows for a mode flipped in the draft. */
export function closedClassChanges(
  persisted: ReadonlySet<number>,
  draft: ReadonlySet<number>,
): LicenseFeatureGroupChange[] {
  return CUSTOMER_FEATURE_ACCESS_CLASSES.flatMap((featureClass) => {
    const was = persisted.has(featureClass.featureTypeCode)
    const is = draft.has(featureClass.featureTypeCode)
    if (was === is) return []
    return [
      {
        kind: "changed" as const,
        label: featureClass.label,
        detail: is
          ? `${ACCESS_MODE_LABEL.open} → ${ACCESS_MODE_LABEL.listed}`
          : `${ACCESS_MODE_LABEL.listed} → ${ACCESS_MODE_LABEL.open}`,
      },
    ]
  })
}

/**
 * After a save: the access read itself, the own list's picks and rules the
 * composer reseeds from, and the package reads whose "also in" and counts
 * a changed membership moves.
 */
export function evictCustomerFeatureAccessQueries(cache: ApolloCache<unknown>) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "getCustomerFeatureAccess" })
  evictFeatureGroupQueries(cache)
}
